import type {
  DomainGroup,
  NodeKey,
  NodeUnit,
  ZoneMeta,
} from 'types/packages-api/slot-valuation-inputs';

/**
 * ── THE ONE PLACE THAT DECIDES WHETHER A TEARSHEET ROW CAN BE EDITED ─────────────────────
 *
 * The affordance design is NOT confirmed, so the
 * rule and its presentation both live here, and changing either is an edit to this file
 * rather than a sweep through the table view. Nothing else may re-derive "is this editable".
 *
 * ── THE RULE IS A CONJUNCTION OF TWO INDEPENDENT FACTS ───────────────────────────────────
 *
 *   DomainGroup.readOnly   the COARSE gate. May a user change anything in this domain at
 *                          all? A property of the domain, not of the node, and the contract
 *                          says outright to read this one for edit controls and never
 *                          `fromPlatformDefaults` (slot-valuation-inputs.ts:534-546).
 *   node.overridable       the FINE gate. Does the override engine accept a field for this
 *                          node? Its VALUE is the engine's field name, which is not always
 *                          the node key — SEVEN of the eleven differ.
 *                          Five need an `afe_` prefix; `afe_source_id` is `afe_header_id` and
 *                          `slot_volume_source` is `volume_source_id`. Always send the VALUE,
 *                          never the node key: a wrong key fails silently (200, no change).
 *
 * Neither implies the other. Expenses is now `readOnly: false` and holds 22 nodes of which
 * only the five AFE cost dates are overridable.
 *
 * ── `overridable` IS ON THE GRAPH, NOT ON THE SLOT ROW, AND THAT SHAPES THIS WHOLE MODULE ─
 *
 * The obvious signature is `(node: SlotInputNode, domain)`, and it cannot work:
 * `SlotInputNode` carries no override field of any kind. `overridable` rides on the NODE
 * DEFINITION in the model-map graph (model_map_graph.rb:67-70), because it is a property of
 * the declared model rather than of this slot's value — one definition, not 176 copies per
 * slot.
 *
 * Two consequences the caller must know about:
 *
 *   1. The rule needs the resolved model map, which is a SECOND request that deliberately
 *      does not block the first paint. Until it lands every node reads as unoverridable and
 *      no marker renders — the same late-arrival behaviour `unit` and `label` already have.
 *      A row cannot therefore be wrongly announced as editable while the map is loading;
 *      it can only be announced late, which is the safe direction.
 *   2. So these take the field itself (or a map to it), never a SlotInputNode.
 */

/** Just enough of a served node definition to answer the fine gate. */
export interface OverridableMeta {
  overridable?: string;
  /**
   * The OverrideFields key to write under, served beside `overridable`.
   * Read through {@link overrideFieldKeyFor}.
   */
  overrideKey?: string;
  /**
   * The `dates` entry carrying this node's per-slot lock — served on the four schedule dates
   * only. Read through {@link isNodeLocked} and {@link nodeKeyForMilestone}.
   */
  milestone?: string;
  /** The registry's declared unit. Read by {@link controlForNode} to pick the editor. */
  unit?: NodeUnit;
}

/** Node key → its served definition. Empty while the model map is still loading. */
export type OverridableByNode = Partial<Record<NodeKey, OverridableMeta>>;

/**
 * Whether the REGISTRY declares an override field for this node.
 *
 * ── `in`, NOT A TRUTHINESS TEST, AND THE CONTRACT INSISTS ON IT ──────────────────────────
 *
 * `overridable` is optional rather than `string | null`: an absent key is silence, a present
 * null would be a claim. The contract says to test it with `'overridable' in node` and never
 * `node.overridable !== null` (slot-valuation-inputs.ts:264-270). A truthiness test would
 * also read a node whose field name is the empty string as unoverridable, which is a
 * different statement from the one the registry made.
 *
 * @param {OverridableMeta} [meta] - The node's served definition, if the map has resolved.
 * @return {boolean} True when the registry declared an override field for it.
 */
export function isNodeOverridable(meta?: OverridableMeta): boolean {
  return Boolean(meta) && 'overridable' in (meta as OverridableMeta);
}

/**
 * The engine field name to send when writing an override for this node.
 *
 * SEND THIS, NOT THE NODE KEY. The registry names an AFE cost date by its source
 * (`afe_drilling_costs_date`) and the engine names it by the cost (`drilling_costs_date`).
 *
 * @param {OverridableMeta} [meta] - The node's served definition.
 * @return {string | null} The engine's field name, or null when the node is not overridable.
 */
export function overrideFieldFor(meta?: OverridableMeta): string | null {
  return isNodeOverridable(meta)
    ? ((meta as OverridableMeta).overridable as string)
    : null;
}

/**
 * The `OverrideFields` KEY to write for this node — the lowerCamelCase spelling of the
 * engine field.
 *
 * ── THREE SPELLINGS, AND ONLY ONE OF THEM IS WRITEABLE ───────────────────────────────────
 *
 *   afe_drilling_costs_date   the NODE key. What the registry calls the row.
 *   drilling_costs_date       the ENGINE field. What `overridable` serves.
 *   drillingCostsDate         the OverrideFields key. What commitSlotOverride must write.
 *
 * ── ⚠️ SERVED, NOT CAMELISED HERE ────────────────────────────────────────────────────────
 *
 * This used to camelise `overridable` and keep an OVERRIDE_KEY_EXCEPTIONS table — a literal
 * copy of SlotOverrideEngine::OVERRIDE_KEY_MAP — for the one field the general rule gets
 * wrong: `afe_header_id` becomes `afeSourceId`, and the camelised `afeHeaderId` is a key
 * AFE_ID_KEYS does not accept, so the write returns 200 and changes nothing. A copy of a
 * backend table whose failure mode is silence is a copy that drifts unnoticed.
 *
 * The server now sends the answer as `overrideKey`, computed by the engine's own
 * `to_display_key`, so there is nothing here to keep in step — which is why the derivation
 * the first half describes is documented and NOT performed.
 *
 * ABSENT MEANS NOT OVERRIDABLE, not "camelise it yourself": `overrideKey` rides with
 * `overridable`, so a node carrying one carries both. A payload from before this key existed
 * returns null and the row simply offers no edit — silence, which is the direction this
 * module fails in everywhere else.
 *
 * ⚠️ TWO JSDoc BLOCKS USED TO BE STACKED HERE, and
 * only the second bound — so the spellings table and the `@param` above were orphaned and
 * invisible to tooling. Merged rather than deleted: both halves say something the other
 * does not.
 *
 * @param {OverridableMeta} [meta] - The node's served definition.
 * @return {string | null} The OverrideFields key, or null when the node is not overridable.
 */
export function overrideFieldKeyFor(meta?: OverridableMeta): string | null {
  return meta?.overrideKey ?? null;
}

/**
 * THE RULE. Whether a user may edit this node's value on this slot.
 *
 * ── A MISSING DOMAIN FAILS CLOSED, DELIBERATELY ──────────────────────────────────────────
 *
 * `!domain?.readOnly` would evaluate to TRUE for a node whose zone shipped no DomainGroup —
 * turning "we were told nothing about this domain" into "editing is permitted", which is the
 * one direction this must never fail in. Absence is silence here exactly as it is for
 * `overridable` itself.
 *
 * UNREACHABLE TODAY, and measured rather than assumed: all nine overridable nodes live in
 * just two zones — the four schedule dates in `development_pace`, the five AFE cost dates in
 * `expenses` — and both zones carry a DomainGroup. This guard is for the payload that stops
 * being true, not for one that exists.
 *
 * NOTE what this does NOT answer. It is a CAPABILITY, not a per-slot state: it does not say
 * whether this slot's value has already been overridden (read `overriddenFields`), nor
 * whether this particular date is locked because the well reported it (read the merged
 * date's `locked`). A date can be editable as a node and locked on a producing well, and the
 * lock is enforced at the point of edit.
 *
 * ── THE TWO ABSENCES, TOLD APART BY THE ZONE ─────────────────────────────────────────────
 *
 * "No DomainGroup arrived" used to mean one thing here: refuse. It is really two, and they
 * want opposite answers. Four zones — type_curve, volumes, cashflow, ownership_interest —
 * have NO DOMAIN BY DESIGN (ZONE_DOMAINS maps them to nil), so there is no domain-level
 * prohibition to respect and the node's own `overridable` is the whole rule. A DomainGroup
 * that SHOULD have arrived and did not is a broken payload, and refusing is still right.
 *
 * The zone carries the server's own answer: `domain` is always emitted, a string when the
 * zone has one and an explicit null when it has none. So a present null is permission to fall
 * through to the fine gate, and a MISSING key — an older cached graph that predates this —
 * still fails closed. Absence is silence here exactly as it is for `overridable` itself.
 *
 * @param {OverridableMeta} [meta] - The node's served definition.
 * @param {DomainGroup | null} [domain] - The DomainGroup for that node's zone, if any.
 * @param {ZoneMeta | null} [zone] - That zone's served metadata, which says whether it has a
 *   domain at all. Omit it and the old fail-closed behaviour is unchanged.
 * @return {boolean} True when the row should offer an edit affordance.
 */
export function isNodeEditable(
  meta?: OverridableMeta,
  domain?: DomainGroup | null,
  zone?: ZoneMeta | null
): boolean {
  if (domain) {
    return !domain.readOnly && isNodeOverridable(meta);
  }
  // No DomainGroup. Only a zone that SAYS it has none may fall through; anything else —
  // including a zone that names a domain whose group failed to arrive — stays shut.
  if (zone && 'domain' in zone && zone.domain === null) {
    return isNodeOverridable(meta);
  }
  return false;
}

/**
 * ── THE MARKER'S COPY, AND WHY IT IS NOT HOVER-ONLY ──────────────────────────────────────
 *
 * The requirement from the start is that a user knows a row is editable BEFORE touching
 * it. A hover-only affordance fails that twice over: it is invisible in a screenshot — which
 * is how this page gets discussed in Slack and pasted into tickets — and it is unreachable
 * without a pointer.
 *
 * So the marker renders as persistent text in the block header, and carries its explanation
 * in a visually hidden sibling that `aria-describedby` points at
 * (tearsheet-zone-section.tsx:151-165).
 *
 * ⚠️ THIS USED TO SAY `title`, ON A FOCUSABLE ELEMENT.
 * The markup no longer has either: a `title` is announced inconsistently on a
 * non-interactive element, and `tabIndex={0}` on a static label gives a keyboard user a stop
 * that does nothing. `aria-describedby` is announced with the label, needs no focus and no
 * JavaScript, and shows a pointer user nothing.
 */
export const EDIT_AFFORDANCE_LABEL = 'Editable';

/** The marker's expansion, shown on hover/focus and read out by assistive tech. */
export const EDIT_AFFORDANCE_TITLE =
  'Some values in this section can be changed. Edits are previewed immediately and applied on the next run.';

// ── THE LOCK, WHICH IS A PER-SLOT FACT AND NOT A CAPABILITY ──────────────────────────────
//
// `isNodeEditable` says the FIELD can be edited on this page. It does not say this slot's
// value may be changed right now: a vendor-actual well REPORTS its milestones, and those come
// back `source: 'onFile'` with `locked: true`. The contract spells the distinction out — "a
// date can be overridable as a node and locked on a producing well".
//
// The lock lives on `response.dates`, keyed by MilestoneKey, so a node has to be mapped to a
// milestone to find it.
//
// ── ⚠️ SERVED AS `milestone`, NOT DECLARED HERE ──────────────────────────────────────────
//
// This file used to carry a literal copy of OverrideWriteValidator::DATE_FIELD_TO_MILESTONE,
// kept because one of the four is irregular: `completion_date`'s milestone is
// `modeledCompletionDate`, because the blob carries no completion date and the schedule
// models one. Deriving it by name would silently produce `completionDate`, match no
// milestone, read `undefined`, and leave a reported completion date editable on a producing
// well — a lock defeated by a naming convention.
//
// The server sends it on the node now, from that same validator constant, so the copy is
// gone rather than kept in step. The five AFE cost dates still carry none, for the reason
// they always did: they are AFE inputs, not well milestones, and no lock is reported for
// them.
//
// ⚠️ TWO JSDoc BLOCKS USED TO SIT HERE ABOVE NO DECLARATION
// — both describing the map that was deleted. Merged into one, and then, because one
// orphaned JSDoc is still an orphaned JSDoc, written as line comments: this is a section
// note about the two declarations below it, not documentation of either. The content is kept
// because the REASON the map was irregular is why the served key must not be re-derived.

/** Just enough of a MergedDate to answer the lock question. */
export interface LockableDate {
  locked?: boolean;
}

/**
 * Whether this node's value is locked on THIS slot.
 *
 * Absent milestone, absent `dates`, or a node that is not a milestone at all → NOT locked.
 * That is the right default here and is the opposite of `isNodeEditable`'s: the lock is a
 * positive claim the payload makes ("the well reported this"), so its absence is genuinely
 * "no lock reported" rather than silence to be feared. The capability gate has already run
 * by the time this is asked.
 *
 * ⚠️ TAKES THE NODE'S SERVED DEFINITION, NOT ITS KEY. The milestone used to come from a
 * literal map here; it is now `meta.milestone`, so this function cannot be asked about a node
 * whose definition has not arrived — which is the honest shape, because without the map there
 * is no milestone to look up and the old signature hid that behind a key it could not resolve.
 *
 * @param {OverridableMeta} [meta] - The node's served definition, if the map has resolved.
 * @param {Record<string, LockableDate>} [dates] - The payload's `dates`, if present.
 * @return {boolean} True when the well reports this milestone and it may not be overridden.
 */
export function isNodeLocked(
  meta?: OverridableMeta,
  dates?: Record<string, LockableDate>
): boolean {
  const milestone = meta?.milestone;
  if (!milestone || !dates) {
    return false;
  }
  return Boolean(dates[milestone]?.locked);
}

/** Which editor a row opens. `null` means: overridable, but no control exists for it yet. */
export type EditControl = 'date' | 'afeSource' | 'volumeSource';

/**
 * WHICH EDITOR THIS NODE OPENS, derived from what the registry already serves.
 *
 * ── DERIVED, NOT A SECOND DECLARATION ────────────────────────────────────────────────────
 *
 * `unit` already travels on every graph node and already says `date` for nine of the eleven
 * overridable ones. A `control:` key beside `overridable` in ModelMap would be a second
 * statement of the same fact, free to disagree with the first — the drift this epic keeps
 * finding. So the unit decides, and only the exceptions are named.
 *
 * ── THE DEFAULT IS NOTHING, NOT A DATE ───────────────────────────────────────────────────
 *
 * Falling back to the date editor for an unrecognised field is how a real value ends up under
 * the wrong control: an AFE identity in a date picker writes a date into an id. So a node this
 * function does not recognise returns `null` and renders NO control. A new overridable field
 * is inert until someone names its editor here, which is a visible gap rather than a silent
 * mis-edit.
 *
 * @param {OverridableMeta} [meta] - The node's served definition.
 * @return {(EditControl|null)} The editor to open, or null when none exists for this node.
 */
export function controlForNode(meta?: OverridableMeta): EditControl | null {
  if (!isNodeOverridable(meta)) {
    return null;
  }
  if (meta?.unit === 'date') {
    return 'date';
  }
  // The non-date overrides, NAMED rather than inferred from the node key. Each has its own
  // picker because each chooses from a different catalogue.
  const field = overrideFieldFor(meta);
  if (field === 'afe_header_id') {
    return 'afeSource';
  }
  if (field === 'volume_source_id') {
    return 'volumeSource';
  }
  return null;
}

/**
 * The registry node whose value a milestone row is showing.
 *
 * INVERTED FROM THE SERVED `milestone`, never re-declared. The Milestones table renders four
 * of the nine overridable DATES a SECOND time (the eleven overridable fields include two that
 * are not dates), so an override marker has to reach it or the most-read rendering of those
 * dates is the one that says nothing. Finding the node means going milestone -> node, the
 * opposite direction to the lock lookup.
 *
 * ⚠️ A SEARCH OVER THE SERVED MAP, NOT AN INVERTED LITERAL. It used to invert
 * LOCK_MILESTONE_BY_NODE, which was itself a copy of the backend's
 * DATE_FIELD_TO_MILESTONE. Both directions now read the one fact the
 * server sends, so `completion_date`'s irregular `modeledCompletionDate` has exactly one
 * place to be got wrong and it is not here.
 *
 * Four entries carry a milestone out of 221, so the scan is over a handful of keys per call
 * and runs four times per render of the Milestones table.
 *
 * @param {string} milestone - A MilestoneKey, e.g. 'spudDate'.
 * @param {Partial<Record<string, OverridableMeta>>} [byNode] - The served node definitions.
 * @return {string|undefined} The node key, or undefined for a milestone with no node.
 */
export function nodeKeyForMilestone(
  milestone: string,
  byNode?: Partial<Record<string, OverridableMeta>>
): string | undefined {
  if (!byNode) {
    return undefined;
  }
  return Object.keys(byNode).find(
    (nodeKey) => byNode[nodeKey]?.milestone === milestone
  );
}
